import { type CourseAttributes, type CourseDataBlock } from 'src/api/generated/ApiClient';
import { type OtherSession } from 'src/types/classSession';

import { formatSessionDate } from './formatSessionDate';

export const findResourceForInstance = (
  included: CourseDataBlock[],
  instanceId: string,
): CourseDataBlock | undefined => {
  return included.find((item) => item.type === 'resource' && item.instanceId === instanceId);
};

export const findEnrollmentForInstance = (
  included: CourseDataBlock[],
  instanceId: string,
): CourseDataBlock | undefined => {
  return included.find((item) => {
    if (item.type !== 'learningObjectInstanceEnrollment') {
      return false;
    }

    // Check top-level instanceId first (for backwards compatibility)
    if (item.instanceId === instanceId) {
      return true;
    }

    // Check relationships.loInstance.data.id which has format "course:XXXX_INSTANCEID"
    const loInstanceId = item.relationships?.loInstance?.data?.id;

    if (loInstanceId) {
      const parts = loInstanceId.split('_');
      const extractedInstanceId = parts[parts.length - 1];

      return extractedInstanceId === instanceId;
    }

    return false;
  });
};

export const isEnrolledInInstance = (courseData: CourseDataBlock, instanceId: string): boolean => {
  return courseData.relationships?.enrollment?.data?.instanceId === instanceId;
};

export const isCompletedEnrollment = (included: CourseDataBlock[], instanceId: string): boolean => {
  const enrollment = findEnrollmentForInstance(included, instanceId);

  return enrollment?.attributes?.state === 'COMPLETED';
};

export const extractSessionDateInfo = (attrs: CourseAttributes) => {
  const dateStart = attrs.dateStart || null;
  const dateEnd = attrs.completionDeadline || null;
  const duration = attrs.desiredDuration || 0;
  const formatted = formatSessionDate(dateStart, dateEnd, duration);

  return { dateStart, dateEnd, duration, ...formatted };
};

export const extractOtherSessions = (
  included: CourseDataBlock[],
  currentInstanceId: string,
  courseId: string,
): OtherSession[] => {
  const now = new Date();

  return included
    .filter(
      (item) =>
        item.type === 'resource' &&
        item.instanceId &&
        item.instanceId !== currentInstanceId &&
        item.attributes?.dateStart &&
        new Date(item.attributes.dateStart) >= now,
    )
    .sort((a, b) => new Date(a.attributes!.dateStart!).getTime() - new Date(b.attributes!.dateStart!).getTime())
    .map((resource) => ({
      id: resource.instanceId!,
      ...extractSessionDateInfo(resource.attributes!),
      detailsUrl: `/class/${courseId}/session/${resource.instanceId}`,
      partnerSiteUrl: 'https://training.atlassian.com', // TODO: Replace with actual partner URL
    }));
};

/**
 * Finds the next upcoming instance by looking at resources with dateStart.
 * Returns null if no future scheduled sessions exist.clear
 */
export const findNextUpcomingInstanceId = (included: CourseDataBlock[]): string | null => {
  const now = new Date();

  const upcomingResource = included
    .filter(
      (item) =>
        item.type === 'resource' &&
        item.instanceId &&
        item.attributes?.dateStart &&
        new Date(item.attributes.dateStart) >= now,
    )
    .sort((a, b) => new Date(a.attributes!.dateStart!).getTime() - new Date(b.attributes!.dateStart!).getTime())[0];

  return upcomingResource?.instanceId || null;
};
